'use client';

import { useCallback, useRef } from 'react';
import {
  AutoSizer,
  CellMeasurer,
  InfiniteLoader,
  type MasonryCellProps,
  Masonry as VirtualizedMasonry,
  WindowScroller,
} from 'react-virtualized';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { TAB_TYPE_MAP } from '@/app/constants';
import { useExpose, useMasonry } from '@/app/hooks';
import type { ActivityInteractionParams, WorkWithTask } from '@/app/types';
import { mapTypeToExploreTab, reportEvent } from '@/app/utils';
import { createPositioner } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import { SCROLL_ELEMENT_ID } from '../ActivityPageClient';
import { ActivityCard } from './ActivityCard';

interface ActivityFeedProps {
  works: WorkWithTask[];
  hasMore: boolean;
  onLoadMore: () => Promise<void>;
  onVote: (params: Omit<ActivityInteractionParams, 'obj_type'>) => void;
  onUnVote: (params: Omit<ActivityInteractionParams, 'obj_type'>) => void;
  interactionPosition: string;
  activityId: string;
  workTab: string;
}

const COLUMNS_PRESET = 'activity' as const;

const isRowLoaded =
  (items: WorkWithTask[]) =>
  ({ index }: { index: number }) => {
    return !!items[index];
  };

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  works,
  hasMore,
  onLoadMore,
  onVote,
  onUnVote,
  interactionPosition,
  activityId,
  workTab,
}) => {
  const { isMounted, scrollElement, masonryRef, cellPositioner, onResize, cache } = useMasonry({
    scrollElementId: SCROLL_ELEMENT_ID,
    columnsPreset: COLUMNS_PRESET,
  });
  const pathname = usePathname();
  const activityCardRef = useRef<HTMLDivElement[]>([]);

  useExpose(
    (idx) => {
      const item = works[idx];
      const type = mapTypeToExploreTab(item.work?.type);
      reportEvent('submission_show', {
        submit_id: item.work?.id || '',
        submit_type: TAB_TYPE_MAP[type],
        show_position: interactionPosition || pathname,
        index: idx,
      });
    },
    activityCardRef.current,
    works,
    [0.1, 1],
  );

  const cellRenderer = useCallback(
    ({ index, key, parent, style }: MasonryCellProps) => {
      const item = works[index];

      if (!item) {
        return (
          <CellMeasurer cache={cache.current} index={index} key={key} parent={parent}>
            <div style={style}>
              <div className="relative mb-1 overflow-hidden rounded-lg">
                <div className="aspect-video w-full animate-pulse rounded-lg bg-gray-300/40" />
              </div>
            </div>
          </CellMeasurer>
        );
      }

      return (
        <CellMeasurer cache={cache.current} index={index} key={key} parent={parent}>
          <div
            style={style}
            ref={(el: HTMLDivElement) => {
              activityCardRef.current[index] = el;
            }}
            data-index={index}
          >
            <ActivityCard
              item={item}
              onVote={onVote}
              onUnVote={onUnVote}
              interactionPosition={interactionPosition}
              activityId={activityId}
              workTab={workTab}
              position={index + 1}
            />
          </div>
        </CellMeasurer>
      );
    },
    [works, cache, onVote, onUnVote, interactionPosition, activityId, workTab],
  );

  const loadMoreRows = useCallback(async () => {
    if (hasMore) {
      await onLoadMore();
    }
  }, [hasMore, onLoadMore]);

  if (!isMounted || !scrollElement) {
    return (
      <div className="min-h-[200px]">
        <GlobalLoader />
      </div>
    );
  }

  return (
    <WindowScroller scrollElement={scrollElement}>
      {({ height, scrollTop }) => (
        <InfiniteLoader
          isRowLoaded={isRowLoaded(works)}
          loadMoreRows={loadMoreRows}
          rowCount={hasMore ? works.length + 1 : works.length}
          threshold={5}
        >
          {({ onRowsRendered }) => (
            <AutoSizer disableHeight onResize={onResize}>
              {({ width }) => {
                if (!cellPositioner.current) {
                  cellPositioner.current = createPositioner({
                    width,
                    cache: cache.current,
                    columnsPreset: COLUMNS_PRESET,
                  });
                }

                const cellCount = hasMore ? works.length + 1 : works.length;

                return (
                  <VirtualizedMasonry
                    autoHeight
                    ref={masonryRef}
                    cellCount={cellCount}
                    cellMeasurerCache={cache.current}
                    cellPositioner={cellPositioner.current}
                    cellRenderer={cellRenderer}
                    height={height}
                    width={width}
                    overscanByPixels={400}
                    scrollTop={scrollTop}
                    key={`masonry-${width}`}
                    onCellsRendered={({ stopIndex }) => {
                      onRowsRendered({
                        startIndex: 0,
                        stopIndex,
                      });
                    }}
                  />
                );
              }}
            </AutoSizer>
          )}
        </InfiniteLoader>
      )}
    </WindowScroller>
  );
};
